import * as Sentry from '@sentry/react';
import { isNoiseEvent } from '@/modules/sentry-noise';
import type { router } from './routes';

const IS_DEV = !!import.meta.env.VITE_IS_LOCALHOST;
const IS_PROD = !!import.meta.env.VITE_BACKEND_IS_PRODUCTION;

const DSN =
  'https://3de7c6998c92a48786a41b9299f56c33@o436067.ingest.us.sentry.io/4512027525382144';

if (!IS_DEV) {
  Sentry.init({
    dsn: DSN,
    environment: IS_PROD ? 'production' : 'stage',
    release: __COMMIT_HASH__,
    tunnel: '/tunnel',
    // Explicit mirror of the SDK's legacy sendDefaultPii:true resolution
    // (defaultPiiToCollectionOptions): every category on, frameContextLines 7 —
    // the option's own default is 5, so omitting it would change behavior.
    dataCollection: {
      userInfo: true,
      cookies: true,
      httpHeaders: { request: true, response: true },
      httpBodies: [
        'incomingRequest',
        'outgoingRequest',
        'incomingResponse',
        'outgoingResponse',
      ],
      urlQueryParams: true,
      graphQL: { document: true, variables: true },
      genAI: { inputs: true, outputs: true },
      databaseQueryData: true,
      stackFrameVariables: true,
      frameContextLines: 7,
    },
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    integrations: [
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
      Sentry.httpClientIntegration(),
    ],
    beforeSend(event, hint) {
      return isNoiseEvent(event, hint.originalException) ? null : event;
    },
  });
}

// The router module loads after this init on purpose — importing it here
// would put its whole module graph ahead of the error capture — so the
// tracing integration joins once the router exists.
export function attachRouterTracing(appRouter: typeof router): void {
  if (IS_DEV) return;
  Sentry.addIntegration(
    Sentry.tanstackRouterBrowserTracingIntegration(appRouter),
  );
}
